(() => {
  if (customElements.get('td-feature-highlights')) return;

  const SELECTORS = {
    swiperHost: '[data-td-feature-swiper]',
    videos: '[data-td-feature-video-scope] video'
  };
  const SWIPER_POLL_INTERVAL_MS = 250;
  const SWIPER_POLL_MAX_ATTEMPTS = 20;
  const VISIBILITY_THRESHOLD = 0.35;
  const REDUCED_MOTION_QUERY = '(prefers-reduced-motion: reduce)';

  class TdFeatureHighlights extends HTMLElement {
    constructor() {
      super();

      this.videos = [];
      this.observer = null;
      this.swiper = null;
      this.swiperPollTimer = null;
      this.swiperPollAttempts = 0;
      this.onSwiperSync = null;

      this.reducedMotionQuery = window.matchMedia(REDUCED_MOTION_QUERY);

      this.handleVisibilityChange = this.handleVisibilityChange.bind(this);
      this.handleViewportChange = this.handleViewportChange.bind(this);
      this.handleReducedMotionChange = this.handleReducedMotionChange.bind(this);
    }

    connectedCallback() {
      this.videos = Array.from(this.querySelectorAll(SELECTORS.videos));
      if (!this.videos.length) return;

      this.setupVideos();
      this.setupObserver();
      this.bindEvents();
      this.bindSwiperHooks();
      this.syncVideos();
    }

    disconnectedCallback() {
      this.unbindEvents();
      this.unbindSwiperHooks();

      if (this.observer) {
        this.observer.disconnect();
        this.observer = null;
      }
    }

    setupVideos() {
      this.videos.forEach((video) => {
        this.applyVideoAttrs(video);

        if (video.readyState < 2) {
          video.addEventListener('loadeddata', () => this.syncVideo(video), { once: true });
        }
      });
    }

    setupObserver() {
      if (!('IntersectionObserver' in window)) return;

      this.observer = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && entry.intersectionRatio >= VISIBILITY_THRESHOLD) {
            this.playVideo(entry.target);
          } else {
            this.pauseVideo(entry.target);
          }
        });
      }, { threshold: [0, VISIBILITY_THRESHOLD, 0.75] });

      this.videos.forEach((video) => this.observer.observe(video));
    }

    bindEvents() {
      document.addEventListener('visibilitychange', this.handleVisibilityChange);

      if (!this.observer) {
        window.addEventListener('resize', this.handleViewportChange);
        window.addEventListener('scroll', this.handleViewportChange, { passive: true });
      }

      if (typeof this.reducedMotionQuery.addEventListener === 'function') {
        this.reducedMotionQuery.addEventListener('change', this.handleReducedMotionChange);
      } else if (typeof this.reducedMotionQuery.addListener === 'function') {
        this.reducedMotionQuery.addListener(this.handleReducedMotionChange);
      }
    }

    unbindEvents() {
      document.removeEventListener('visibilitychange', this.handleVisibilityChange);
      window.removeEventListener('resize', this.handleViewportChange);
      window.removeEventListener('scroll', this.handleViewportChange);

      if (typeof this.reducedMotionQuery.removeEventListener === 'function') {
        this.reducedMotionQuery.removeEventListener('change', this.handleReducedMotionChange);
      } else if (typeof this.reducedMotionQuery.removeListener === 'function') {
        this.reducedMotionQuery.removeListener(this.handleReducedMotionChange);
      }
    }

    bindSwiperHooks() {
      const swiperHost = this.querySelector(SELECTORS.swiperHost);
      if (!swiperHost) return;

      const attachSwiperEvents = () => {
        const swiper = swiperHost.swiper;
        if (!swiper) return false;
        if (this.swiper === swiper) return true;

        this.unbindSwiperHooks();

        this.swiper = swiper;
        this.onSwiperSync = () => this.syncVideos();

        swiper.on('slideChangeTransitionEnd', this.onSwiperSync);
        swiper.on('transitionEnd', this.onSwiperSync);
        this.syncVideos();
        return true;
      };

      if (attachSwiperEvents()) return;

      this.clearSwiperPollTimer();
      this.swiperPollAttempts = 0;
      this.swiperPollTimer = window.setInterval(() => {
        this.swiperPollAttempts += 1;
        if (attachSwiperEvents() || this.swiperPollAttempts >= SWIPER_POLL_MAX_ATTEMPTS) {
          this.clearSwiperPollTimer();
        }
      }, SWIPER_POLL_INTERVAL_MS);
    }

    unbindSwiperHooks() {
      if (this.swiper && this.onSwiperSync && typeof this.swiper.off === 'function') {
        this.swiper.off('slideChangeTransitionEnd', this.onSwiperSync);
        this.swiper.off('transitionEnd', this.onSwiperSync);
      }

      this.clearSwiperPollTimer();
      this.swiper = null;
      this.onSwiperSync = null;
    }

    clearSwiperPollTimer() {
      if (!this.swiperPollTimer) return;
      window.clearInterval(this.swiperPollTimer);
      this.swiperPollTimer = null;
    }

    handleVisibilityChange() {
      if (document.hidden) {
        this.videos.forEach((video) => this.pauseVideo(video));
        return;
      }

      this.syncVideos();
    }

    handleViewportChange() {
      this.syncVideos();
    }

    handleReducedMotionChange() {
      this.syncVideos();
    }

    applyVideoAttrs(video) {
      video.autoplay = true;
      video.defaultMuted = true;
      video.muted = true;
      video.loop = true;
      video.playsInline = true;

      video.setAttribute('autoplay', '');
      video.setAttribute('muted', '');
      video.setAttribute('loop', '');
      video.setAttribute('playsinline', '');
    }

    playVideo(video) {
      if (!video || !video.isConnected) return;
      if (document.hidden || this.reducedMotionQuery.matches) {
        this.pauseVideo(video);
        return;
      }

      this.applyVideoAttrs(video);
      const playPromise = video.play();
      if (playPromise && typeof playPromise.catch === 'function') {
        playPromise.catch(() => {});
      }
    }

    pauseVideo(video) {
      if (!video || video.paused) return;
      video.pause();
    }

    isVideoVisible(video, threshold = VISIBILITY_THRESHOLD) {
      const rect = video.getBoundingClientRect();
      if (rect.width <= 0 || rect.height <= 0) return false;

      const viewportWidth = window.innerWidth || document.documentElement.clientWidth;
      const viewportHeight = window.innerHeight || document.documentElement.clientHeight;
      const visibleWidth = Math.max(0, Math.min(rect.right, viewportWidth) - Math.max(rect.left, 0));
      const visibleHeight = Math.max(0, Math.min(rect.bottom, viewportHeight) - Math.max(rect.top, 0));
      const visibleArea = visibleWidth * visibleHeight;
      const totalArea = rect.width * rect.height;

      return totalArea > 0 && visibleArea / totalArea >= threshold;
    }

    syncVideo(video) {
      if (!video || !video.isConnected) return;
      if (this.isVideoVisible(video)) {
        this.playVideo(video);
      } else {
        this.pauseVideo(video);
      }
    }

    syncVideos() {
      this.videos = this.videos.filter((video) => video.isConnected);
      this.videos.forEach((video) => this.syncVideo(video));
    }
  }

  customElements.define('td-feature-highlights', TdFeatureHighlights);
})();
